import { AxiosResponse } from "axios";
import {
  AltiusRes,
  CreateDealNoteData,
  ICreateNoteDatabox,
  IHandleItemEventType,
  INote,
  IUpdateNoteDatabox,
  DealStageItemNote,
  UpdateDealNoteData
} from "store/types";
import { DealAction } from "..";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface INoteActions {
  createNoteAction: (data: ICreateNoteDatabox) => Promise<INote>;
  getNotesAction: (dealId: number) => Promise<void>;
  getNoteAction: (id: number) => Promise<INote>;
  getNoteStoreAction: (id: number) => Promise<void>;
  updateNoteAction: (data: IUpdateNoteDatabox) => Promise<void>;
  deleteNoteAction: (id: number) => Promise<void>;
  getNoteMetaAction: (noteId: number) => Promise<void>;
  invalidateNoteMetaAction: () => void;
  setNoteLockAction: (id: number, type: "start" | "finish") => Promise<void>;

  createDealNoteAction: (data: CreateDealNoteData) => Promise<DealStageItemNote>;
  updateDealNoteAction: (data: UpdateDealNoteData) => Promise<DealStageItemNote>;
}

export class NoteActions extends AsyncActions implements INoteActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public handleNote = (note: INote, event: IHandleItemEventType): void => {
    this.dispatch({
      type: DealAction.HandleNoteSuccess,
      payload: { item: note, event }
    });
  };

  public createNoteAction = async (data: ICreateNoteDatabox): Promise<INote> => {
    try {
      const { entityId, entityType, text = "", ...rest } = data;
      const {
        data: { data: payload }
      }: AxiosResponse<AltiusRes<INote>> = await this.api.post({
        url: "/api/v0.0.3/notes",
        data: {
          entity_type: entityType,
          entity_id: entityId,
          text,
          ...rest
        }
      });
      this.handleNote(payload, "add");
      return payload;
    } catch (error) {
      throw error;
    }
  };

  public getNotesAction = async (dealId: number): Promise<void> => {
    try {
      const {
        data: { data }
      }: AxiosResponse<AltiusRes<Record<number, INote>>> = await this.api.get({
        url: `/api/v0.0.3/deals/${dealId}/notes`
      });
      this.dispatch({ type: DealAction.GetNotesSuccess, payload: data });
    } catch (error) {
      throw error;
    }
  };

  public deleteNoteAction = async (id: number): Promise<void> => {
    try {
      const {
        data: { data: payload }
      }: AxiosResponse<AltiusRes<INote>> = await this.api.delete({
        url: `/api/v0.0.3/notes/${id}`
      });
      this.handleNote(payload, "delete");
    } catch (error) {
      throw error;
    }
  };

  public getNoteAction = async (id: number): Promise<INote> => {
    const {
      data: { data: payload }
    }: AxiosResponse<AltiusRes<INote>> = await this.api.get({
      url: `/api/v0.0.3/notes/${id}`
    });
    return payload;
  };

  public getNoteStoreAction = async (id: number): Promise<void> => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const note = await this.getNoteAction(id);
      this.handleNote(note, "store");
    } catch (error) {
      throw error;
    }
  };

  public updateNoteAction = async (data: IUpdateNoteDatabox): Promise<void> => {
    try {
      const { id, ...body } = data;
      const {
        data: { data: payload }
      }: AxiosResponse<AltiusRes<INote>> = await this.api.patch({
        url: `/api/v0.0.3/notes/${id}`,
        data: body
      });
      this.handleNote(payload, "update");
    } catch (error) {
      throw error;
    }
  };

  public getNoteMetaAction = async (noteId: number): Promise<void> => {
    try {
      const {
        data: { data: payload }
      } = await this.api.get({
        url: `/api/v0.0.3/notes/${noteId}`,
        params: {
          ["as-meta"]: true
        }
      });
      this.dispatch({ type: DealAction.GetNoteMetaSuccess, payload });
    } catch (error) {
      throw error;
    }
  };

  public invalidateNoteMetaAction: INoteActions["invalidateNoteMetaAction"] = () => {
    this.dispatch({ type: DealAction.InvalidateNoteMeta, payload: null });
  };

  public setNoteLockAction: INoteActions["setNoteLockAction"] = async (id, type) => {
    await this.api.post({
      url: `/api/v0.0.3/notes/${id}/editing/${type}`
    });
  };

  public createDealNoteAction: INoteActions["createDealNoteAction"] = async ({ dealId, ...body }) => {
    const {
      data: { data }
    }: AxiosResponse<AltiusRes<DealStageItemNote>> = await this.api.post({
      url: `/api/v0.0.3/deals/${dealId}/note`,
      data: body
    });
    return data;
  };

  public updateDealNoteAction: INoteActions["updateDealNoteAction"] = async ({ dealId, id, ...body }) => {
    const {
      data: { data }
    }: AxiosResponse<AltiusRes<DealStageItemNote>> = await this.api.patch({
      url: `/api/v0.0.3/deals/${dealId}/note/${id}`,
      data: body
    });
    return data;
  };
}
